// src/components/admin/DigrnitariesManager.tsx
// DROP 18f — Dignitaries Directory Upload and Management
// Platform Admin + authorized uploaders only

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Crown, Plus, Edit2, Trash2, Upload, Search,
  CheckCircle, AlertTriangle, RefreshCw, Eye,
  EyeOff, Download, User, Phone, Mail, X, Save
} from "lucide-react";

interface Dignitary {
  id: string;
  name: string;
  title: string;
  rank?: string;
  position?: string;
  category: "Royal"|"Government"|"Diaspora"|"Traditional"|"Religious"|"Corporate"|"Cultural"|"Other";
  village?: string;
  phone?: string;
  email?: string;
  photo?: string;
  bio?: string;
  notes?: string;
  isPublic: boolean;
  displayOrder: number;
  addedBy: string;
  addedAt: string;
  updatedAt?: string;
}

const CATEGORIES: Dignitary["category"][] = [
  "Royal","Government","Diaspora","Traditional","Religious","Corporate","Cultural","Other"
];

const CAT_COLORS: Record<string, { bg:string; text:string; border:string }> = {
  Royal:       { bg:"rgba(201,168,76,0.1)",  text:"#C9A84C",  border:"rgba(201,168,76,0.3)"  },
  Government:  { bg:"rgba(59,130,246,0.1)",  text:"#93C5FD",  border:"rgba(59,130,246,0.3)"  },
  Diaspora:    { bg:"rgba(139,92,246,0.1)",  text:"#C4B5FD",  border:"rgba(139,92,246,0.3)"  },
  Traditional: { bg:"rgba(245,158,11,0.1)",  text:"#FCD34D",  border:"rgba(245,158,11,0.3)"  },
  Religious:   { bg:"rgba(16,185,129,0.1)",  text:"#6EE7B7",  border:"rgba(16,185,129,0.3)"  },
  Corporate:   { bg:"rgba(239,68,68,0.1)",   text:"#FCA5A5",  border:"rgba(239,68,68,0.3)"   },
  Cultural:    { bg:"rgba(236,72,153,0.1)",  text:"#F9A8D4",  border:"rgba(236,72,153,0.3)"  },
  Other:       { bg:"rgba(148,163,184,0.1)", text:"#CBD5E1",  border:"rgba(148,163,184,0.3)" },
};

const BLANK: Partial<Dignitary> = {
  name:"", title:"", rank:"", position:"", category:"Traditional",
  village:"", phone:"", email:"", photo:"", bio:"", notes:"", isPublic:true, displayOrder:0
};

export default function DigrnitariesManager() {
  const [dignitaries, setDignitaries] = useState<Dignitary[]>([]);
  const [search,      setSearch]      = useState("");
  const [filterCat,   setFilterCat]   = useState<string>("all");
  const [loading,     setLoading]     = useState(false);
  const [msg,         setMsg]         = useState<{type:"ok"|"err";text:string}|null>(null);
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState<Dignitary|null>(null);
  const [form,        setForm]        = useState<Partial<Dignitary>>(BLANK);
  const [uploading,   setUploading]   = useState(false);
  const [showPrivate, setShowPrivate] = useState(false);
  const photoRef = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem("erue_auth_token") || "";
  const flash  = (type:"ok"|"err", text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),5000);
  };
  const api = useCallback(async (url:string, opts:RequestInit={}) => {
    const res = await fetch(url,{...opts,headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`, ...(opts.headers||{})}});
    return res.json();
  },[]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api("/api/admin/dignitaries-full");
      if (d.success) setDignitaries(d.dignitaries||[]);
    } finally { setLoading(false); }
  },[api]);

  useEffect(()=>{ load(); },[load]);

  const upd = (field: keyof Dignitary, val: unknown) =>
    setForm(p => ({...p, [field]:val}));

  const openNew = () => {
    setEditing(null); setForm({...BLANK}); setShowForm(true);
  };

  const openEdit = (d: Dignitary) => {
    setEditing(d); setForm({...d}); setShowForm(true);
  };

  const uploadPhoto = async (file: File): Promise<string|null> => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "dignitaries");
      const res = await fetch("/api/community-portals/upload",{
        method:"POST", headers:{ Authorization:`Bearer ${token()}` }, body:fd
      });
      const d = await res.json();
      return d.success && d.url ? d.url : null;
    } catch { return null; }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim() || !form.title?.trim()) {
      flash("err","Name and title are required"); return;
    }
    const url    = editing ? `/api/admin/dignitaries-full/${editing.id}` : "/api/admin/dignitaries-full";
    const method = editing ? "PUT" : "POST";
    const d = await api(url, { method, body:JSON.stringify(form) });
    if (d.success) {
      flash("ok", editing ? "Dignitary updated" : "Dignitary added");
      setShowForm(false); load();
    } else flash("err", d.error||"Failed");
  };

  const togglePublic = async (dig: Dignitary) => {
    const d = await api(`/api/admin/dignitaries-full/${dig.id}`,{
      method:"PUT", body:JSON.stringify({...dig, isPublic:!dig.isPublic})
    });
    if (d.success) { flash("ok",`${!dig.isPublic?"Made public":"Set to private"}`); load(); }
    else flash("err",d.error||"Failed");
  };

  const deleteDig = async (id: string) => {
    if (!confirm("Delete this dignitary record?")) return;
    const d = await api(`/api/admin/dignitaries-full/${id}`,{ method:"DELETE" });
    if (d.success) { flash("ok","Deleted"); load(); }
    else flash("err",d.error||"Failed");
  };

  const filtered = dignitaries
    .filter(d => filterCat==="all" || d.category===filterCat)
    .filter(d => !search ||
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.title.toLowerCase().includes(search.toLowerCase()) ||
      (d.village||"").toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Dignitaries Directory
          </h2>
          <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
            {dignitaries.length} records · {dignitaries.filter(d=>d.isPublic).length} public
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setShowPrivate(!showPrivate)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all"
                  style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
            {showPrivate ? <Eye size={13}/> : <EyeOff size={13}/>}
            {showPrivate ? "Showing contacts" : "Hide contacts"}
          </button>
          <button onClick={openNew}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                  style={{ background:"#C9A84C", color:"#070D0A" }}>
            <Plus size={13}/> Add Dignitary
          </button>
        </div>
      </div>

      {/* Flash */}
      {msg && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
             style={{ background:msg.type==="ok"?"rgba(74,222,128,0.1)":"rgba(239,68,68,0.1)", border:`1px solid ${msg.type==="ok"?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)"}`, color:msg.type==="ok"?"#4ADE80":"#FCA5A5" }}>
          {msg.type==="ok"?<CheckCircle size={14}/>:<AlertTriangle size={14}/>} {msg.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color:"rgba(237,232,220,0.3)" }}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, title, village…"
                 className="pl-7 pr-3 py-1.5 rounded-lg text-xs outline-none"
                 style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC", width:"200px" }}/>
        </div>
        <div className="flex gap-1 flex-wrap">
          {["all",...CATEGORIES].map(cat => (
            <button key={cat} onClick={()=>setFilterCat(cat)}
                    className="px-2.5 py-1.5 rounded-lg text-xs border transition-all capitalize"
                    style={{
                      background: filterCat===cat ? (cat==="all"?"#C9A84C":(CAT_COLORS[cat]?.bg||"#C9A84C")) : "transparent",
                      borderColor: filterCat===cat ? (cat==="all"?"#C9A84C":(CAT_COLORS[cat]?.border||"#C9A84C")) : "#1C2E22",
                      color: filterCat===cat ? (cat==="all"?"#070D0A":(CAT_COLORS[cat]?.text||"#EDE8DC")) : "rgba(237,232,220,0.4)",
                    }}>
              {cat}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-1.5 rounded-lg border ml-auto" style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.4)" }}>
          <RefreshCw size={13} className={loading?"animate-spin":""}/>
        </button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
          <Crown size={32} className="mx-auto mb-2 opacity-30"/>
          {search||filterCat!=="all" ? "No matches found" : "No dignitaries added yet"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(d => {
            const cc = CAT_COLORS[d.category]||CAT_COLORS.Other;
            return (
              <div key={d.id} className="p-4 rounded-xl border transition-all"
                   style={{ background:"#121E16", borderColor:"#1C2E22" }}>
                <div className="flex items-start gap-3">
                  {/* Photo */}
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
                       style={{ background:"rgba(15,81,50,0.4)" }}>
                    {d.photo ? (
                      <img src={d.photo} alt={d.name} className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-bold text-lg"
                           style={{ color:"#C9A84C" }}>
                        {d.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color:"#EDE8DC" }}>{d.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full border"
                            style={{ background:cc.bg, color:cc.text, borderColor:cc.border, fontSize:"9px" }}>
                        {d.category}
                      </span>
                      {!d.isPublic && (
                        <span className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background:"rgba(239,68,68,0.1)", color:"#FCA5A5", fontSize:"9px" }}>
                          Private
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color:"#C9A84C" }}>{d.title}</p>
                    {d.position && <p className="text-xs" style={{ color:"rgba(237,232,220,0.5)" }}>{d.position}</p>}
                    {d.village && <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>📍 {d.village}</p>}

                    {/* Private contact info */}
                    {showPrivate && (
                      <div className="mt-1.5 space-y-0.5">
                        {d.phone && <p className="text-xs flex items-center gap-1" style={{ color:"rgba(237,232,220,0.5)" }}><Phone size={10}/>{d.phone}</p>}
                        {d.email && <p className="text-xs flex items-center gap-1" style={{ color:"rgba(237,232,220,0.5)" }}><Mail size={10}/>{d.email}</p>}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button onClick={()=>openEdit(d)} style={{ color:"#C9A84C" }}><Edit2 size={13}/></button>
                    <button onClick={()=>togglePublic(d)}
                            style={{ color:d.isPublic?"#4ADE80":"rgba(237,232,220,0.3)" }}>
                      {d.isPublic ? <Eye size={13}/> : <EyeOff size={13}/>}
                    </button>
                    <button onClick={()=>deleteDig(d.id)} style={{ color:"rgba(239,68,68,0.5)" }}><Trash2 size={13}/></button>
                  </div>
                </div>

                {d.bio && (
                  <p className="text-xs mt-2 line-clamp-2" style={{ color:"rgba(237,232,220,0.4)" }}>{d.bio}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── FORM MODAL ──────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-xl bg-[#0E1812] border border-[#C9A84C]/25 rounded-2xl p-6 mt-8 mb-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
                {editing ? "Edit Dignitary" : "Add Dignitary"}
              </h3>
              <button onClick={()=>{ setShowForm(false); setEditing(null); setForm(BLANK); }}
                      style={{ color:"rgba(237,232,220,0.4)" }}><X size={18}/></button>
            </div>

            <div className="space-y-3">
              {/* Photo upload */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0"
                     style={{ background:"rgba(15,81,50,0.4)" }}>
                  {form.photo ? (
                    <img src={form.photo} alt="Photo" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ color:"#C9A84C" }}>
                      <User size={24}/>
                    </div>
                  )}
                </div>
                <div>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden"
                         onChange={async e=>{
                           const f = e.target.files?.[0];
                           if (f) {
                             const url = await uploadPhoto(f);
                             if (url) upd("photo", url);
                           }
                         }}/>
                  <button onClick={()=>photoRef.current?.click()} disabled={uploading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border"
                          style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
                    <Upload size={12}/>{uploading?"Uploading…":"Upload Photo"}
                  </button>
                  <p className="text-xs mt-1" style={{ color:"rgba(237,232,220,0.3)" }}>JPG, PNG — max 5MB</p>
                </div>
              </div>

              {/* Name + Title */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Full Name *</label>
                  <input value={form.name||""} onChange={e=>upd("name",e.target.value)} placeholder="Full name"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Title / Honorific *</label>
                  <input value={form.title||""} onChange={e=>upd("title",e.target.value)}
                         placeholder="e.g. HRH, Chief, Dr., Alhaji"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              </div>

              {/* Category + Position */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Category *</label>
                  <select value={form.category||"Traditional"} onChange={e=>upd("category",e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                    {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Position / Role</label>
                  <input value={form.position||""} onChange={e=>upd("position",e.target.value)}
                         placeholder="e.g. Minister of Finance"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              </div>

              {/* Village + Rank */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Village / Community</label>
                  <input value={form.village||""} onChange={e=>upd("village",e.target.value)}
                         placeholder="e.g. Jattu-Uzairue"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Rank / Grade</label>
                  <input value={form.rank||""} onChange={e=>upd("rank",e.target.value)}
                         placeholder="e.g. SAN, OFR, MFR"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              </div>

              {/* Contact (private) */}
              <div className="p-3 rounded-xl" style={{ background:"rgba(239,68,68,0.04)", border:"1px solid rgba(239,68,68,0.1)" }}>
                <p className="text-xs mb-2 flex items-center gap-1.5" style={{ color:"rgba(239,68,68,0.7)" }}>
                  <EyeOff size={11}/> Private contact details — not shown publicly
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Phone</label>
                    <input value={form.phone||""} onChange={e=>upd("phone",e.target.value)} type="tel"
                           placeholder="+234 000 000 0000"
                           className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                           style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Email</label>
                    <input value={form.email||""} onChange={e=>upd("email",e.target.value)} type="email"
                           placeholder="private@email.com"
                           className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                           style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Biography / Note</label>
                <textarea value={form.bio||""} onChange={e=>upd("bio",e.target.value)} rows={3}
                          placeholder="Brief biography or note about this dignitary"
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>

              {/* Public toggle + Display order */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isPublic??true} onChange={e=>upd("isPublic",e.target.checked)} className="accent-yellow-400"/>
                  <span className="text-sm" style={{ color:"rgba(237,232,220,0.7)" }}>Show publicly on website</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>Order:</span>
                  <input type="number" value={form.displayOrder||0} onChange={e=>upd("displayOrder",Number(e.target.value))}
                         className="w-16 px-2 py-1.5 rounded-lg text-sm outline-none text-center"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button onClick={()=>{ setShowForm(false); setEditing(null); setForm(BLANK); }}
                        className="flex-1 py-2.5 rounded-xl text-sm border"
                        style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>Cancel</button>
                <button onClick={save}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                        style={{ background:"#C9A84C", color:"#070D0A" }}>
                  <Save size={14}/>{editing ? "Update" : "Add Dignitary"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
